import { useState, useEffect, useMemo } from "react";
import PortalLayout from "@/components/PortalLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Ship, Anchor, Gauge, AlertTriangle, Star, Activity, TrendingUp,
  ArrowRight, Clock, MapPin, Users as UsersIcon, FileWarning, Bell,
  ChevronRight
} from "lucide-react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import {
  MOCK_VESSELS, MOCK_CERTIFICATES, MOCK_VOYAGES, MOCK_NOTICES,
  PLATFORM_KPIS, MOCK_SATISFACTION_DATA, MOCK_EMISSIONS, VESSEL_STATUS_COLORS,
  OFFSHORE_ASSETS, resolveClientKey,
  getClientVessels, getClientVoyages, getClientCertificates, getClientAssets,
} from "@/data/portalMockData";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid,
} from "recharts";

// Lazy load leaflet
import { useRef } from "react";

const VesselMap = ({ vessels, assets }: { vessels: typeof MOCK_VESSELS; assets: typeof OFFSHORE_ASSETS }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let map: any;
    const loadMap = async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");

      if (!mapRef.current) return;

      map = L.map(mapRef.current, {
        center: [0, -20],
        zoom: 2,
        zoomControl: false,
        attributionControl: false,
      });

      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 19,
      }).addTo(map);
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 19,
      }).addTo(map);

      L.control.zoom({ position: "bottomright" }).addTo(map);

      vessels.forEach((v) => {
        const statusInfo = VESSEL_STATUS_COLORS[v.status] || VESSEL_STATUS_COLORS.available;
        const color = statusInfo.bg;
        const isMoving = v.status === "in_transit";
        const pulseRing = isMoving ? `<div style="position:absolute;top:-4px;left:-4px;width:20px;height:20px;border-radius:50%;border:2px solid ${color};opacity:0.4;animation:ping 1.5s cubic-bezier(0,0,0.2,1) infinite;"></div>` : "";
        const icon = L.divIcon({
          className: "",
          html: `<div style="position:relative;width:14px;height:14px;">${pulseRing}<div style="position:absolute;top:1px;left:1px;width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 8px ${color}80;"></div></div>`,
          iconSize: [14, 14],
          iconAnchor: [7, 7],
        });
        const statusLabel = statusInfo.label;
        const alongsideInfo = v.status === "alongside" ? `<div style="font-size:11px;color:#28A745;font-weight:600;margin:4px 0;">⚓ Alongside ${v.destination || ""}</div>` : "";
        const transitInfo = v.status === "in_transit" ? `<div style="font-size:11px;color:#0066CC;font-weight:600;margin:4px 0;">🚢 En route to ${v.destination || ""}</div>` : "";
        const shoreInfo = v.status === "available" ? `<div style="font-size:11px;color:#17A2B8;font-weight:600;margin:4px 0;">🏠 At Shore Base</div>` : "";
        const paxLine = v.paxOnBoard > 0
          ? `<div style="font-size:11px;margin:2px 0;">👥 <b>${v.paxOnBoard}</b> passengers on board</div>`
          : `<div style="font-size:11px;color:#888;margin:2px 0;">No passengers on board</div>`;
        L.marker([v.lat, v.lng], { icon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family:'DM Sans',sans-serif;min-width:240px;max-width:300px;">
              <div style="font-weight:700;font-size:15px;margin-bottom:2px;">${v.name}</div>
              <div style="font-size:11px;color:#666;margin-bottom:6px;">${v.type}</div>
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};"></span>
                <span style="font-size:12px;font-weight:600;">${statusLabel}</span>
              </div>
              ${alongsideInfo}${transitInfo}${shoreInfo}
              <hr style="border:none;border-top:1px solid #eee;margin:6px 0;"/>
              <div style="font-size:11px;margin:2px 0;">🏴 <b>Flag:</b> ${v.flag}</div>
              <div style="font-size:11px;margin:2px 0;">⚡ <b>Speed:</b> ${v.currentSpeed > 0 ? v.currentSpeed + " kts" : "0 kts (stationary)"}</div>
              <div style="font-size:11px;margin:2px 0;">👨‍✈️ <b>Captain:</b> ${v.captain}</div>
              <div style="font-size:11px;margin:2px 0;">🔧 <b>Chief Engineer:</b> ${v.chiefEngineer}</div>
              ${paxLine}
              <hr style="border:none;border-top:1px solid #eee;margin:6px 0;"/>
              <div style="font-size:10px;color:#aaa;">IMO: ${v.imoNumber} · MMSI: ${v.mmsi}</div>
              <div style="font-size:10px;color:#aaa;">${v.lat.toFixed(2)}°${v.lat >= 0 ? "N" : "S"}, ${Math.abs(v.lng).toFixed(2)}°${v.lng >= 0 ? "E" : "W"}</div>
            </div>
          `, { maxWidth: 320 });
      });

      // Add offshore assets with operator-status differentiation
      const assetColors: Record<string, string> = { FPSO: "#E74C3C", Drillship: "#3498DB", Terminal: "#FF9F1C", "Semi-Sub": "#F39C12" };
      assets.forEach((asset) => {
        const color = assetColors[asset.type] || "#fff";
        const opStatus = asset.operatorStatus || "operated";
        const size = asset.type === "Terminal" ? 18 : 16;
        let shape: string;

        if (asset.type === "Terminal") {
          const opacity = opStatus === "future" ? "0.4" : opStatus === "non-operated" ? "0.6" : "1";
          const border = opStatus !== "operated" ? "stroke-dasharray:2,2;" : "";
          shape = `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" style="filter:drop-shadow(0 0 4px ${color}80);opacity:${opacity};"><path d="M12 2L12 10M12 10C9.79 10 8 11.79 8 14M12 10C14.21 10 16 11.79 16 14M8 14H5L7 22H17L19 14H16M8 14H16M6 6H18" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="${border}"/></svg>`;
        } else if (opStatus === "future") {
          const br = asset.type === "FPSO" ? "0" : "50%";
          const tr = asset.type === "FPSO" ? "transform:rotate(45deg);" : "";
          shape = `<div style="width:${size}px;height:${size}px;border-radius:${br};background:transparent;border:2px dashed ${color};box-shadow:0 0 10px ${color}40;${tr}"></div>`;
        } else if (opStatus === "non-operated") {
          const br = asset.type === "FPSO" ? "0" : "50%";
          const tr = asset.type === "FPSO" ? "transform:rotate(45deg);" : "";
          shape = `<div style="width:${size}px;height:${size}px;border-radius:${br};background:${color}55;border:2px dashed #fff;box-shadow:0 0 10px ${color}60;${tr}"></div>`;
        } else {
          shape = asset.type === "FPSO"
            ? `<div style="width:${size}px;height:${size}px;background:${color};border:2px solid #fff;box-shadow:0 0 10px ${color}80;transform:rotate(45deg);"></div>`
            : `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 10px ${color}80;"></div>`;
        }
        const icon = L.divIcon({ className: "", html: shape, iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
        const statusLabels: Record<string, string> = { operated: "🟢 Operated", "non-operated": "🟡 Non-Operated Interest", future: "🔵 Under Construction" };
        const statusLine = opStatus !== "operated" ? `<div style="font-size:11px;font-weight:600;margin:4px 0;color:${opStatus === "non-operated" ? "#F1C40F" : "#3498DB"};">${statusLabels[opStatus]}</div>` : "";
        const operatorLine = asset.operatedBy ? `<div style="font-size:11px;color:#aaa;">Operator: ${asset.operatedBy} · ${asset.equityPct}% interest</div>` : "";
        const capacityLine = asset.capacityBpd ? `<div style="font-size:11px;color:#aaa;">Capacity: ${(asset.capacityBpd / 1000).toFixed(0)}K bpd</div>` : "";
        L.marker([asset.lat, asset.lng], { icon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family:'DM Sans',sans-serif;min-width:180px;">
              <div style="font-weight:700;font-size:14px;">${asset.name}</div>
              <div style="font-size:11px;color:#888;">${asset.type} · ${asset.region}</div>
              ${statusLine}
              ${asset.field ? `<div style="font-size:11px;color:#aaa;">Field: ${asset.field}</div>` : ""}
              ${operatorLine}${capacityLine}
            </div>
          `);
      });

      // Fit bounds
      const allPoints = [
        ...vessels.map((v) => [v.lat, v.lng] as [number, number]),
        ...assets.map((a) => [a.lat, a.lng] as [number, number]),
      ];
      const bounds = L.latLngBounds(allPoints);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });

      setMapReady(true);
    };
    loadMap();
    return () => { map?.remove(); };
  }, [vessels, assets]);

  return (
    <div className="relative rounded-xl overflow-hidden border border-[hsl(220,15%,90%)]" style={{ height: 380 }}>
      {!mapReady && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[hsl(220,20%,97%)]">
          <div className="w-6 h-6 border-2 border-[hsl(211,100%,40%)]/30 border-t-[hsl(211,100%,40%)] rounded-full animate-spin" />
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
      {/* Legend overlay */}
      <div className="absolute bottom-3 left-3 z-[1000] pointer-events-auto">
        <div className="bg-black/75 backdrop-blur-sm rounded-lg px-3 py-2 text-white text-[11px] leading-relaxed">
          <div className="font-bold text-xs mb-1">Asset Types</div>
          <div><span className="text-[#E74C3C] mr-1.5">◆</span>FPSO</div>
          <div><span className="text-[#3498DB] mr-1.5">●</span>Drillship</div>
          <div><span className="text-[#FF9F1C] mr-1.5">⚓</span>Terminal / Port</div>
          <div><span className="text-[#F39C12] mr-1.5">●</span>Semi-Sub</div>
          <div className="border-t border-white/20 my-1.5" />
          <div className="font-bold text-xs mb-1">Operator Status</div>
          <div>🟢 Operated</div>
          <div>🟡 Non-Operated Interest</div>
          <div>🔵 Future / Under Construction</div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { fullName } = useAuth();
  const { companyName } = usePortalCompany();
  const clientKey = resolveClientKey(companyName);

  const clientVessels = useMemo(() => clientKey ? getClientVessels(clientKey) : MOCK_VESSELS, [clientKey]);
  const clientCerts = useMemo(() => clientKey ? getClientCertificates(clientKey) : MOCK_CERTIFICATES, [clientKey]);
  const clientVoyageList = useMemo(() => clientKey ? getClientVoyages(clientKey) : MOCK_VOYAGES, [clientKey]);
  const clientAssets = useMemo(() => clientKey ? getClientAssets(clientKey) : OFFSHORE_ASSETS, [clientKey]);

  const statusCounts = useMemo(() => ({
    on_charter: clientVessels.filter((v) => v.status === "on_charter").length,
    standby: clientVessels.filter((v) => v.status === "standby").length,
    in_transit: clientVessels.filter((v) => v.status === "in_transit").length,
    alongside: clientVessels.filter((v) => v.status === "alongside").length,
    available: clientVessels.filter((v) => v.status === "available").length,
  }), [clientVessels]);

  const urgentCerts = clientCerts
    .filter((c) => c.daysRemaining <= 90)
    .sort((a, b) => a.daysRemaining - b.daysRemaining);

  const recentVoyages = clientVoyageList
    .filter((v) => v.status === "completed" || v.status === "in_progress")
    .slice(0, 5);

  const emissionsData = MOCK_EMISSIONS.monthly.slice(-3);

  // Live satisfaction data
  const { data: liveSurveys = [] } = useQuery({
    queryKey: ["dashboard-satisfaction"],
    queryFn: async () => {
      const { data } = await supabase
        .from("satisfaction_surveys" as any)
        .select("*")
        .order("submitted_at", { ascending: false })
        .limit(50);
      return (data || []) as any[];
    },
  });

  const liveOverallScore = useMemo(() => {
    if (!liveSurveys.length) return MOCK_SATISFACTION_DATA.overallScore;
    return Math.round((liveSurveys.reduce((s: number, r: any) => s + (r.rating_overall || 0), 0) / liveSurveys.length) * 10) / 10;
  }, [liveSurveys]);

  const liveTrends = useMemo(() => {
    if (!liveSurveys.length) return MOCK_SATISFACTION_DATA.trends;
    const monthMap: Record<string, { overall: number[]; crew: number[]; punctuality: number[] }> = {};
    liveSurveys.forEach((s: any) => {
      const d = new Date(s.submitted_at);
      const key = d.toLocaleString("en", { month: "short" });
      if (!monthMap[key]) monthMap[key] = { overall: [], crew: [], punctuality: [] };
      if (s.rating_overall) monthMap[key].overall.push(s.rating_overall);
      if (s.rating_crew) monthMap[key].crew.push(s.rating_crew);
      if (s.rating_punctuality) monthMap[key].punctuality.push(s.rating_punctuality);
    });
    const avg = (arr: number[]) => arr.length ? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10 : null;
    return Object.entries(monthMap).map(([month, d]) => ({
      month,
      overall: avg(d.overall),
      crew: avg(d.crew),
      punctuality: avg(d.punctuality),
    }));
  }, [liveSurveys]);

  return (
    <PortalLayout>
      {/* Vel-iO Branded Hero */}
      <div className="relative mb-6 overflow-hidden rounded-2xl border border-white/10 shadow-xl"
           style={{ background: "linear-gradient(120deg, hsl(218 55% 10%) 0%, hsl(215 50% 16%) 55%, hsl(211 100% 28%) 100%)" }}>
        {/* Decorative grid + glow */}
        <div className="absolute inset-0 opacity-[0.07]"
             style={{ backgroundImage: "linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-30" style={{ background: "hsl(180 80% 50%)" }} />
        <div className="absolute -bottom-24 left-1/3 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: "hsl(75 90% 60%)" }} />
        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />

        <div className="relative grid md:grid-cols-[auto_1fr] gap-6 md:gap-8 items-center px-6 md:px-10 py-7 md:py-9">
          {/* Left: Vel-iO logo on white card */}
          <div className="bg-white rounded-xl px-3 py-2 shadow-lg flex items-center justify-center self-start md:self-center">
            <img src="/vel-io-logo.png" alt="Vel-iO" className="h-9 md:h-12 w-auto object-contain" />
          </div>

          {/* Right: Welcome content */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-[2px] bg-gradient-to-r from-cyan-400 to-lime-400" />
              <span className="text-cyan-300 text-[10px] font-bold tracking-[3px] uppercase">A Velocity Offshore Platform</span>
            </div>
            <p className="text-white/70 text-sm md:text-base font-light tracking-[3px] uppercase mb-2">Client Portal</p>
            <h1 className="font-display text-2xl md:text-4xl font-bold text-white tracking-tight leading-tight">
              Welcome back, <span className="text-cyan-300">{fullName || "Client"}</span>
            </h1>
            <p className="text-white/60 text-sm mt-2 max-w-xl">
              Real-time intelligence across your <span className="text-white/90 font-medium">Velocity Offshore</span> fleet — vessels, voyages, compliance, and crew, unified.
            </p>
          </div>
        </div>
      </div>

      {/* Fleet Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {[
          { label: "In Transit", count: statusCounts.in_transit, color: "#0066CC", icon: Ship },
          { label: "Alongside", count: statusCounts.alongside, color: "#28A745", icon: Anchor },
          { label: "At Shore Base", count: statusCounts.available, color: "#17A2B8", icon: Gauge },
          { label: "Standby", count: statusCounts.standby, color: "#FFC107", icon: AlertTriangle },
          { label: "On Hire", count: statusCounts.on_charter, color: "#28A745", icon: Activity },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="portal-card p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + "18" }}>
                <Icon className="w-5 h-5" style={{ color: s.color }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[hsl(215,25%,17%)]">{s.count}</p>
                <p className="text-[11px] text-[hsl(215,12%,50%)]">{s.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* KPI Ticker Strip */}
      <div className="portal-card p-3 mb-6 flex flex-wrap items-center gap-4 md:gap-6 text-center">
        {[
          { label: "LTI-Free Days", value: PLATFORM_KPIS.ltiFreeDays.toString(), color: "#28A745" },
          { label: "Fleet Availability", value: PLATFORM_KPIS.fleetAvailability + "%", color: "#0066CC" },
          { label: "On-Time Departure", value: PLATFORM_KPIS.onTimeDeparture + "%", color: "#0066CC" },
          { label: "Open NCRs", value: PLATFORM_KPIS.openNCRs.toString(), color: "#FFC107" },
          { label: "NPS", value: PLATFORM_KPIS.nps.toString(), color: "#28A745" },
          { label: "TRIR", value: PLATFORM_KPIS.trir.toFixed(2), color: "#28A745" },
        ].map((kpi, i) => (
          <div key={kpi.label} className={`flex-1 min-w-[80px] ${i > 0 ? "border-l border-[hsl(220,15%,90%)]" : ""}`}>
            <p className="text-lg md:text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
            <p className="text-[9px] md:text-[10px] text-[hsl(215,12%,50%)] uppercase tracking-wider">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Main Content — two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
        {/* Left 60% — Map */}
        <div className="lg:col-span-3">
          <div className="portal-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-[hsl(215,25%,17%)]">Live Vessel Map</h2>
              <div className="flex items-center gap-3 text-[10px]">
                {Object.entries(VESSEL_STATUS_COLORS).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: val.bg }} />
                    <span className="text-[hsl(215,12%,50%)]">{val.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <VesselMap vessels={clientVessels} assets={clientAssets} />
          </div>
        </div>

        {/* Right 40% — Certificate + Voyage Feed */}
        <div className="lg:col-span-2 space-y-4">
          {/* Certificate Expiry Widget */}
          <div className="portal-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-[hsl(215,25%,17%)]">Certificate Expiry — Next 90 Days</h2>
              <Link to="/portal/hseq" className="text-[10px] text-[hsl(211,100%,40%)] hover:underline flex items-center gap-1">
                View All <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            {urgentCerts.length === 0 ? (
              <p className="text-sm text-[hsl(215,12%,50%)] text-center py-4">All certificates current ✓</p>
            ) : (
              <div className="space-y-2">
                {urgentCerts.map((cert) => (
                  <div key={cert.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-[hsl(220,20%,97%)]">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      cert.status === "expired" ? "rag-red-bg" : cert.status === "expiring" ? "rag-amber-bg" : "rag-green-bg"
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[hsl(215,25%,17%)] truncate">{cert.name}</p>
                      <p className="text-[10px] text-[hsl(215,12%,50%)]">{cert.vessel}</p>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      cert.status === "expired"
                        ? "bg-red-100 text-red-700"
                        : "bg-amber-100 text-amber-700"
                    }`}>
                      {cert.daysRemaining <= 0 ? `${Math.abs(cert.daysRemaining)}d overdue` : `${cert.daysRemaining}d`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Voyage Activity */}
          <div className="portal-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-[hsl(215,25%,17%)]">Recent Voyage Activity</h2>
              <Link to="/portal/voyages" className="text-[10px] text-[hsl(211,100%,40%)] hover:underline flex items-center gap-1">
                View All <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="space-y-2">
              {recentVoyages.map((voy) => (
                <div key={voy.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-[hsl(220,20%,97%)]">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    voy.status === "in_progress" ? "bg-[#0066CC]" : "bg-[#28A745]"
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[hsl(215,25%,17%)] truncate">
                      {voy.voyageId} — {voy.vessel}
                    </p>
                    <p className="text-[10px] text-[hsl(215,12%,50%)]">
                      {voy.origin} → {voy.destination} · {voy.pax} pax
                    </p>
                  </div>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${
                    voy.status === "in_progress"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-green-100 text-green-700"
                  }`}>
                    {voy.status.replace("_", " ")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row — 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Satisfaction Trend */}
        <div className="portal-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-[hsl(215,25%,17%)]">Satisfaction Score Trend</h2>
            <Link to="/portal/satisfaction" className="text-[10px] text-[hsl(211,100%,40%)] hover:underline">Details</Link>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
            <span className="text-2xl font-bold text-[hsl(215,25%,17%)]">{liveOverallScore}</span>
            <span className="text-xs text-[hsl(215,12%,50%)]">/ 5</span>
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={liveTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#999" }} axisLine={false} tickLine={false} />
              <YAxis domain={[3, 5]} tick={{ fontSize: 10, fill: "#999" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #eee' }} />
              <Line type="monotone" dataKey="overall" stroke="#0066CC" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="crew" stroke="#28A745" strokeWidth={1} dot={false} strokeDasharray="4 4" />
              <Line type="monotone" dataKey="punctuality" stroke="#FFC107" strokeWidth={1} dot={false} strokeDasharray="4 4" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Emissions */}
        <div className="portal-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-[hsl(215,25%,17%)]">Monthly Emissions (MT CO₂)</h2>
            <Link to="/portal/esg" className="text-[10px] text-[hsl(211,100%,40%)] hover:underline">Details</Link>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={emissionsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#999" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#999" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #eee' }} />
              {clientVessels.slice(0, 3).map((v, i) => {
                const key = v.name.replace("Velocity ", "").toLowerCase();
                const colors = ["#0066CC", "#28A745", "#FFC107", "#17A2B8", "#DC3545", "#6F42C1", "#E83E8C", "#FD7E14"];
                return <Bar key={key} dataKey={key} name={v.name.replace("Velocity ", "")} fill={colors[i % colors.length]} radius={[3, 3, 0, 0]} />;
              })}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Unread Notices */}
        <div className="portal-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-[hsl(215,25%,17%)]">Notices</h2>
            <Link to="/portal/messages" className="text-[10px] text-[hsl(211,100%,40%)] hover:underline">View All</Link>
          </div>
          <div className="space-y-2">
            {MOCK_NOTICES.map((notice) => (
              <div key={notice.id} className={`p-3 rounded-lg ${notice.read ? "bg-[hsl(220,20%,97%)]" : "bg-blue-50 border border-blue-100"}`}>
                <div className="flex items-start gap-2">
                  {!notice.read && <span className="w-1.5 h-1.5 rounded-full bg-[#0066CC] mt-1.5 flex-shrink-0" />}
                  <div>
                    <p className={`text-xs ${notice.read ? "text-[hsl(215,12%,50%)]" : "text-[hsl(215,25%,17%)] font-medium"}`}>
                      {notice.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[hsl(220,20%,97%)] text-[hsl(215,12%,50%)]">{notice.category}</span>
                      <span className="text-[9px] text-[hsl(215,12%,50%)]">{notice.date}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default Dashboard;
